import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HiUpload, HiDownload, HiClipboardList, HiEyeOff } from 'react-icons/hi';
import { SimpleButton, Input, Select, useStateContext, useHttp } from '@veda/dashboard-ui';
import { usePppApi } from '../services/pppApi';
import { useAuthUser } from '../hooks/useAuthUser';

function PrimaryButton({ isLoading, customFunc, text, disabled, icon }) {
  return (
    <button
      type="button"
      onClick={customFunc}
      disabled={isLoading || disabled}
      style={{ backgroundColor: '#478389', border: 'none', outline: 'none' }}
      className="px-3.5 py-1.5 flex justify-center gap-1.5 items-center w-fit text-xs font-medium rounded transition-all duration-150 active:scale-95 text-white disabled:opacity-60 hover:opacity-90"
    >
      {isLoading ? (
        <svg className="animate-spin h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ) : icon ? (
        <span className="h-4 w-4 flex items-center justify-center">{icon}</span>
      ) : null}
      {text}
    </button>
  );
}

const TIPO_LABELS = {
  nomina_rup:                'Nomina RUP (art. 15 D.lgs 36/2023)',
  dip:                       'DIP',
  avviso:                    'Avviso Pubblico',
  proposta_tecnica:          'Proposta Tecnica (PFTE)',
  proposta_economica:        'Proposta Economica',
  studio_fattibilita:        'Studio di Fattibilità',
  piano_economico_finanziario: 'Piano Economico Finanziario',
  schema_contratto:          'Schema di Contratto',
  relazione_tecnica:         'Relazione Tecnica',
  pfte:                      'PFTE',
  documentazione_gara:       'Documentazione di Gara',
  verbale:                   'Verbale',
  delibera:                  'Delibera',
  determinazione:            'Determinazione',
  altro:                     'Altro',
};

const STATO_CONFIG = {
  bozza:        { label: 'Bozza',       css: 'bg-gray-100 text-gray-700' },
  in_revisione: { label: 'In revisione', css: 'bg-yellow-100 text-yellow-800' },
  approvato:    { label: 'Approvato',   css: 'bg-green-100 text-green-700' },
  pubblicato:   { label: 'Pubblicato',  css: 'bg-blue-100 text-blue-700' },
};

const TRANSIZIONI = {
  bozza:        ['in_revisione', 'approvato'],
  in_revisione: ['approvato', 'bozza'],
  approvato:    ['pubblicato', 'bozza'],
  pubblicato:   [],
};

const STATO_BADGE_HOVER = {
  bozza:        'hover:bg-gray-200 hover:text-gray-800',
  in_revisione: 'hover:bg-yellow-200 hover:text-yellow-900',
  approvato:    'hover:bg-green-200 hover:text-green-800',
  pubblicato:   'hover:bg-blue-200 hover:text-blue-800',
};

function StateBadgeActions({ transizioni, onCambia, loading }) {
  if (transizioni.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-gray-100">
      <span className="text-[10px] text-gray-400 mr-0.5">Sposta in:</span>
      {transizioni.map((stato) => {
        const cfg = STATO_CONFIG[stato];
        return (
          <button
            key={stato}
            type="button"
            onClick={() => onCambia(stato)}
            disabled={loading}
            className={`px-2 py-0.5 rounded text-xs font-medium transition-colors disabled:opacity-50 cursor-pointer ${cfg.css} ${STATO_BADGE_HOVER[stato]}`}
          >
            {cfg.label}
          </button>
        );
      })}
      {loading && <span className="text-xs text-gray-400">…</span>}
    </div>
  );
}

function StatoBadge({ stato }) {
  const cfg = STATO_CONFIG[stato] ?? { label: stato, css: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.css}`}>
      {cfg.label}
    </span>
  );
}

function UploadForm({ procedimento, fasi, propostaId, onSuccess, onCancel, _locate }) {
  const api = usePppApi();
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ tipo: '', faseId: '', ufficio: '', riferimento_normativo: '', note: '' });
  const [fileName, setFileName] = useState('');

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setFileName(f ? f.name : '');
  };

  const handleSubmit = () => {
    const file = fileRef.current?.files[0];
    if (!file || !form.tipo) return;

    const fd = new FormData();
    fd.append('file', file);
    fd.append('tipo', form.tipo);
    if (form.faseId) fd.append('faseId', form.faseId);
    if (propostaId) fd.append('propostaId', propostaId);
    if (form.ufficio) fd.append('ufficio', form.ufficio);
    if (form.riferimento_normativo) fd.append('riferimento_normativo', form.riferimento_normativo);
    if (form.note) fd.append('note', form.note);

    setSaving(true);
    api.uploadDocumento(procedimento.id, fd, {
      onSuccess: () => { setSaving(false); onSuccess(); },
      onError:   () => setSaving(false),
    });
  };

  const valid = fileName && form.tipo;

  const tipoOptions = Object.entries(TIPO_LABELS).map(([v, l]) => ({ value: v, label: l }));
  const faseOptions = (fasi ?? []).map((f) => ({ value: String(f.id), label: `Fase ${f.numeroFase}` }));

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-4 max-w-[800px]">
      <p className="text-sm font-semibold text-blue-800">{_locate('ppp_doc_carica_titolo') ?? 'Carica documento'}</p>

      {/* Selezione file */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {_locate('ppp_doc_file') ?? 'File'} <span className="text-red-500">*</span>
        </label>
        <div
          className="flex items-center gap-3 rounded-lg border border-gray-300 bg-white px-3 py-2 cursor-pointer hover:border-blue-400 transition-colors"
          onClick={() => fileRef.current?.click()}
        >
          <HiUpload className="h-4 w-4 text-gray-400 shrink-0" />
          <span className={`text-sm truncate ${fileName ? 'text-gray-800' : 'text-gray-400'}`}>
            {fileName || (_locate('ppp_doc_seleziona_file') ?? 'Seleziona un file…')}
          </span>
          <span className="ml-auto text-xs text-blue-600 font-medium shrink-0">
            {_locate('ppp_doc_sfoglia') ?? 'Sfoglia'}
          </span>
        </div>
        <input ref={fileRef} type="file" className="hidden" onChange={handleFileChange} />
      </div>

      {/* Tipo + Fase */}
      <div className="grid grid-cols-2 gap-3">
        <Select
          label={`${_locate('ppp_doc_tipo')} *`}
          name="tipo"
          value={form.tipo}
          options={tipoOptions}
          onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}
        />
        <Select
          label={_locate('ppp_doc_fase_label')}
          name="faseId"
          value={form.faseId}
          options={[{ value: '', label: '—' }, ...faseOptions]}
          onChange={(e) => setForm((f) => ({ ...f, faseId: e.target.value }))}
        />
      </div>

      {/* Ufficio + Rif. normativo */}
      <div className="grid grid-cols-2 gap-3">
        <Input
          label={_locate('ppp_doc_ufficio')}
          name="ufficio"
          value={form.ufficio}
          onChange={(e) => setForm((f) => ({ ...f, ufficio: e.target.value }))}
        />
        <Input
          label={_locate('ppp_doc_rif_normativo')}
          name="riferimento_normativo"
          value={form.riferimento_normativo}
          onChange={(e) => setForm((f) => ({ ...f, riferimento_normativo: e.target.value }))}
        />
      </div>

      {/* Azioni */}
      <div className="flex justify-end items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded border border-gray-200 bg-white"
        >
          {_locate('ppp_form_annulla')}
        </button>
        <PrimaryButton
          isLoading={saving}
          customFunc={valid ? handleSubmit : undefined}
          disabled={!valid}
          text={_locate('ppp_doc_carica')}
          icon={<HiUpload />}
        />
      </div>
    </div>
  );
}

function NuovaVersioneForm({ procedimento, documento, onSuccess, onCancel, _locate }) {
  const api = usePppApi();
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [fileName, setFileName] = useState('');
  const [note, setNote] = useState('');

  const handleSubmit = () => {
    const file = fileRef.current?.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('file', file);
    if (note) fd.append('note', note);

    setSaving(true);
    api.nuovaVersioneDocumento(procedimento.id, documento.id, fd, {
      onSuccess: () => { setSaving(false); onSuccess(); },
      onError:   () => setSaving(false),
    });
  };

  return (
    <div className="mt-2 border border-amber-200 rounded-lg p-3 bg-amber-50 space-y-2">
      <p className="text-xs font-semibold text-amber-800">
        {_locate('ppp_doc_nuova_versione')} — v{documento.versione + 1}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50"
        >
          Seleziona file
        </button>
        <span className="text-xs text-gray-500">{fileName || '—'}</span>
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFileName(e.target.files[0]?.name ?? '')} />
      </div>
      <input
        type="text"
        placeholder={_locate('ppp_doc_note')}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        className="block w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <div className="flex justify-end gap-2">
        <SimpleButton customFunc={onCancel} text={_locate('ppp_form_annulla')} />
        <PrimaryButton
          isLoading={saving}
          customFunc={fileName ? handleSubmit : undefined}
          text="Carica"
          icon={<HiUpload />}
        />
      </div>
    </div>
  );
}

function DocumentoRow({ documento, procedimento, onUpdate, canUpload, canApprova, canDelete, _locate }) {
  const api = usePppApi();
  const { request } = useHttp();
  const [downloading, setDownloading] = useState(false);
  const [cambiandoStato, setCambiandoStato] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [showNuovaVersione, setShowNuovaVersione] = useState(false);

  const transizioni = TRANSIZIONI[documento.stato] ?? [];

  const handleDownload = () => {
    setDownloading(true);
    request(api.scaricaDocumento(procedimento.id, documento.id), {
      method: 'GET',
      download: true,
      onSuccess: async (response) => {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = documento.nome;
        a.click();
        URL.revokeObjectURL(url);
        setDownloading(false);
      },
      onError: () => setDownloading(false),
    });
  };

  const handleCambiaStato = (nuovoStato) => {
    if (!nuovoStato) return;
    setCambiandoStato(true);
    api.cambiaStatoDocumento(procedimento.id, documento.id, nuovoStato, {
      onSuccess: () => { setCambiandoStato(false); onUpdate(); },
      onError:   () => setCambiandoStato(false),
    });
  };

  const handleElimina = () => {
    if (!window.confirm('Eliminare il documento? Il file viene archiviato (soft delete).')) return;
    setEliminando(true);
    api.eliminaDocumento(procedimento.id, documento.id, {
      onSuccess: () => { setEliminando(false); onUpdate(); },
      onError:   () => setEliminando(false),
    });
  };

  return (
    <div className="border border-gray-200 rounded-lg px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        {/* Info principale */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
              {TIPO_LABELS[documento.tipo] ?? documento.tipo}
            </span>
            <StatoBadge stato={documento.stato} />
            <span className="text-xs text-gray-400">{_locate('ppp_doc_versione')}{documento.versione}</span>
            {documento.hasParent && (
              <span className="text-xs text-gray-400 italic">revisione</span>
            )}
          </div>
          <p className="text-sm font-medium text-gray-800 mt-1 truncate">{documento.nome}</p>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400 flex-wrap">
            {documento.ufficio && <span>{documento.ufficio}</span>}
            {documento.riferimentoNormativo && <span>{documento.riferimentoNormativo}</span>}
            {documento.dimensione && <span>{(documento.dimensione / 1024).toFixed(0)} KB</span>}
            <span>{new Date(documento.createdAt).toLocaleDateString('it-IT')}</span>
          </div>
        </div>

        {/* Azioni */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Download */}
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="text-xs text-blue-600 hover:underline font-medium disabled:opacity-50 px-1 py-1"
          >
            {downloading ? '...' : _locate('ppp_doc_scarica')}
          </button>

          {/* Nuova versione */}
          {canUpload && (
            <button
              onClick={() => setShowNuovaVersione((v) => !v)}
              className="text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded px-2 py-1"
              title={_locate('ppp_doc_nuova_versione')}
            >
              ↑v
            </button>
          )}

          {/* Elimina */}
          {canDelete && (
            <button
              onClick={handleElimina}
              disabled={eliminando}
              className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50 px-1"
              title={_locate('ppp_doc_elimina')}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {canApprova && (
        <StateBadgeActions
          transizioni={transizioni}
          onCambia={handleCambiaStato}
          loading={cambiandoStato}
        />
      )}

      {showNuovaVersione && (
        <NuovaVersioneForm
          procedimento={procedimento}
          documento={documento}
          onSuccess={() => { setShowNuovaVersione(false); onUpdate(); }}
          onCancel={() => setShowNuovaVersione(false)}
          _locate={_locate}
        />
      )}
    </div>
  );
}

function ChecklistPanel({ procedimento, fasi, faseAttiva, _locate }) {
  const api = usePppApi();
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [faseId, setFaseId] = useState(faseAttiva?.id ?? '');

  const fetchChecklist = useCallback((fId) => {
    if (!fId) return;
    setLoading(true);
    api.checklistFase(procedimento.id, fId, {
      onSuccess: (data) => { setChecklist(data); setLoading(false); },
      onError:   ()     => setLoading(false),
    });
  }, [procedimento.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (faseId) fetchChecklist(faseId);
  }, [faseId, fetchChecklist]);

  const completate = checklist.filter((v) => v.completato).length;
  const obbligatorie = checklist.filter((v) => v.obbligatorio);
  const obbligatorieCompletate = obbligatorie.filter((v) => v.completato).length;

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-gray-700">{_locate('ppp_doc_checklist_titolo')}</p>
        <select
          value={faseId}
          onChange={(e) => setFaseId(e.target.value)}
          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm"
        >
          <option value="">{_locate('ppp_doc_seleziona_fase')}</option>
          {(fasi ?? []).map((f) => (
            <option key={f.id} value={f.id}>Fase {f.numeroFase}</option>
          ))}
        </select>
      </div>

      {loading && <p className="text-sm text-gray-400">{_locate('ppp_caricamento')}</p>}

      {!loading && faseId && checklist.length === 0 && (
        <p className="text-sm text-gray-400">{_locate('ppp_doc_checklist_vuota')}</p>
      )}

      {!loading && checklist.length > 0 && (
        <>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>{completate}/{checklist.length} completati</span>
            {obbligatorie.length > 0 && (
              <span className={obbligatorieCompletate === obbligatorie.length ? 'text-green-600 font-medium' : 'text-amber-600 font-medium'}>
                {obbligatorieCompletate}/{obbligatorie.length} obbligatori
              </span>
            )}
          </div>
          <div className="space-y-1.5">
            {checklist.map((voce) => (
              <div
                key={voce.tipoDocumento}
                className={`flex items-center justify-between rounded-md px-3 py-2 text-sm ${
                  voce.completato ? 'bg-green-50' : voce.obbligatorio ? 'bg-red-50' : 'bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className={voce.completato ? 'text-green-600' : voce.obbligatorio ? 'text-red-400' : 'text-gray-400'}>
                    {voce.completato ? '✓' : '○'}
                  </span>
                  <div>
                    <span className="text-gray-800">{TIPO_LABELS[voce.tipoDocumento] ?? voce.tipoDocumento}</span>
                    {voce.riferimentoNormativo && (
                      <p className="text-xs text-gray-400">{voce.riferimentoNormativo}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {voce.documentiCaricati > 0 && (
                    <span className="text-xs text-gray-500">{voce.documentiCaricati} doc</span>
                  )}
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                    voce.obbligatorio ? 'bg-gray-200 text-gray-600' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {voce.obbligatorio ? _locate('ppp_doc_obbligatorio') : _locate('ppp_doc_facoltativo')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function DocumentiTab({ procedimento, propostaId, _locate }) {
  const api = usePppApi();
  const { request } = useHttp();
  const { can, ready: authReady } = useAuthUser();
  const { _locate: _l } = useStateContext();
  const loc = _locate ?? _l;

  const [documenti, setDocumenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const canUpload  = can('ppp.documenti.upload');
  const canApprova = can('ppp.documenti.approve');
  const canDelete  = can('ppp.documenti.delete');

  const fasi = procedimento?.fasi ?? [];
  const faseAttiva = fasi.find((f) => f.stato === 'in_corso') ?? fasi[0] ?? null;

  const fetchDocumenti = useCallback(() => {
    if (!procedimento?.id) return;
    setLoading(true);
    const params = propostaId ? { propostaId } : null;
    api.listDocumenti(procedimento.id, params, {
      onSuccess: (data) => { setDocumenti(data); setLoading(false); },
      onError:   ()     => setLoading(false),
    });
  }, [procedimento?.id, propostaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchDocumenti(); }, [fetchDocumenti]);

  const handleDownloadZip = () => {
    setDownloadingZip(true);
    request(api.scaricaZipDocumenti(procedimento.id), {
      method: 'GET',
      download: true,
      onSuccess: async (response) => {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `documenti_ppp_${procedimento.id}.zip`;
        a.click();
        URL.revokeObjectURL(url);
        setDownloadingZip(false);
      },
      onError: () => setDownloadingZip(false),
    });
  };

  if (loading || !authReady) {
    return <div className="p-4 text-gray-400 text-sm">{loc('ppp_caricamento')}</div>;
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header azioni */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2">
          {canUpload && (
            <SimpleButton
              customFunc={() => { setShowUpload((v) => !v); }}
              text={loc('ppp_doc_carica')}
              icon={<HiUpload className="h-4 w-4" />}
            />
          )}
          <SimpleButton
            customFunc={() => setShowChecklist((v) => !v)}
            text={showChecklist ? loc('ppp_doc_nascondi_checklist') : loc('ppp_doc_mostra_checklist')}
            icon={showChecklist ? <HiEyeOff className="h-4 w-4" /> : <HiClipboardList className="h-4 w-4" />}
          />
        </div>
        <div className="ml-auto">
          {documenti.length > 0 && (
            <PrimaryButton
              isLoading={downloadingZip}
              customFunc={handleDownloadZip}
              text={loc('ppp_doc_scarica_zip')}
              icon={<HiDownload />}
            />
          )}
        </div>
      </div>

      {/* Form upload */}
      {showUpload && canUpload && (
        <UploadForm
          procedimento={procedimento}
          fasi={fasi}
          propostaId={propostaId}
          onSuccess={() => { setShowUpload(false); fetchDocumenti(); }}
          onCancel={() => setShowUpload(false)}
          _locate={loc}
        />
      )}

      {/* Checklist */}
      {showChecklist && (
        <ChecklistPanel
          procedimento={procedimento}
          fasi={fasi}
          faseAttiva={faseAttiva}
          _locate={loc}
        />
      )}

      {/* Lista documenti */}
      {documenti.length === 0 ? (
        <p className="text-sm text-gray-400">{loc('ppp_doc_vuoti')}</p>
      ) : (
        <div className="space-y-2">
          {documenti.map((doc) => (
            <DocumentoRow
              key={doc.id}
              documento={doc}
              procedimento={procedimento}
              onUpdate={fetchDocumenti}
              canUpload={canUpload}
              canApprova={canApprova}
              canDelete={canDelete}
              _locate={loc}
            />
          ))}
        </div>
      )}
    </div>
  );
}
